import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ArrowRight, CalendarDays, Clock, User } from "lucide-react";

import { useGetPendingApprovalsQuery } from "@/services/(company)/attendanceRegularizationApi";
import { DashboardCard } from "@/components/common/DashboardCard";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { statusToVariant } from "@/components/ui/badge/statusToVariant";
import { Skeleton } from "@/components/ui/skeleton";
import { PATHS } from "@/routes/paths";
import { useLocale } from "@/hooks/useLocale";
import type { IPendingApproval } from "@/types/(company)/timeAttendance.interface";

const TYPE_CONFIG: Record<
  IPendingApproval["type"],
  { labelKey: string; statusKey: string; accentClass: string }
> = {
  LEAVE_REQUEST: {
    labelKey: "leave",
    statusKey: "leave",
    accentClass: "border-s-info",
  },
  REGULARIZATION: {
    labelKey: "regularization",
    statusKey: "overtime",
    accentClass: "border-s-warning",
  },
};

const getDateInfo = (
  item: IPendingApproval,
  formatDate: (date: Date | number, pattern?: string) => string,
) => {
  if (item.type === "LEAVE_REQUEST" && item.startDate) {
    const start = formatDate(new Date(item.startDate), "dd MMM");
    const end = item.endDate
      ? formatDate(new Date(item.endDate), "dd MMM yyyy")
      : "";
    return item.startDate === item.endDate ? end : `${start} – ${end}`;
  }
  if (item.requestDate)
    return formatDate(new Date(item.requestDate), "dd MMM yyyy");
  return null;
};

const ApprovalSkeleton = () => (
  <>
    {Array.from({ length: 4 }).map((_, i) => (
      <div key={i} className="flex items-start gap-3 p-3 border rounded-sm">
        <Skeleton className="size-9 rounded-full shrink-0" />
        <div className="flex-1 space-y-1.5">
          <div className="flex justify-between">
            <Skeleton className="h-3.5 w-20 rounded" />
            <Skeleton className="h-4 w-16 rounded-full" />
          </div>
          <Skeleton className="h-3 w-36 rounded" />
          <Skeleton className="h-2.5 w-28 rounded" />
          <Skeleton className="h-2.5 w-32 rounded" />
        </div>
      </div>
    ))}
  </>
);

interface IProps {
  /** See RecentEmployeesTable — the dashboard's fixed half owns the height. */
  className?: string;
}

export const PendingApprovals = ({ className = "h-[520px]!" }: IProps) => {
  const { t } = useTranslation("company");
  const { formatDate } = useLocale();
  const { data: pendingApprovals, isLoading } = useGetPendingApprovalsQuery({
    limit: 10,
  });

  return (
    <DashboardCard
      title={t("company:dashboard.pending_approvals")}
      rootClassname={className}
      contentClassName="overflow-hidden! pt-0! flex flex-col p-0!"
    >
      {/* Scrollable area */}
      <div className="flex-1 min-h-0 overflow-y-auto scrollbar-thin px-4 pt-4 pb-2 flex flex-col gap-3">
        {isLoading ? (
          <ApprovalSkeleton />
        ) : !pendingApprovals?.data?.length ? (
          <div className="flex flex-col items-center justify-center h-32 text-muted-foreground text-sm">
            {t("company:dashboard.no_pending_approvals")}
          </div>
        ) : (
          pendingApprovals.data.map((item) => {
            const config = TYPE_CONFIG[item.type];
            const dateInfo = getDateInfo(item, formatDate);

            return (
              <Link
                key={item.id}
                to={
                  item.type === "LEAVE_REQUEST"
                    ? PATHS.COMPANY.COMPANY_LEAVES_MANAGEMENT_PATHS.LEAVES_SUMMARY(
                        item.id,
                      )
                    : PATHS.COMPANY.COMPANY_TIME_ATTENDANCE_PATHS.VIEW_REGULARIZATION(
                        item.id,
                      )
                }
                className="group block"
              >
                <div
                  className={`flex items-start gap-3 rounded-sm border border-s-[3px] border-border bg-background px-3 py-4 group-hover:bg-muted transition-colors ${config.accentClass}`}
                >
                  <Avatar className="size-9 shrink-0 ring-2 ring-border ring-offset-1">
                    <AvatarImage src={item.profileImage ?? undefined} />
                    <AvatarFallback className="bg-muted text-muted-foreground">
                      <User size={15} />
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0 space-y-0.5">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-semibold leading-tight truncate">
                        {item.fullName.split(" ")[0]}
                      </span>
                      <Badge
                        variant={statusToVariant(config.statusKey)}
                        dot={false}
                        className="shrink-0"
                      >
                        {t(`company:dashboard.${config.labelKey}`)}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground line-clamp-1">
                      {item.summary}
                    </p>
                    {item.reason && (
                      <p className="text-[11px] text-muted-foreground/70 italic line-clamp-1">
                        "{item.reason}"
                      </p>
                    )}
                    <div className="flex items-center gap-3 pt-0.5 text-[11px] text-muted-foreground">
                      {dateInfo && (
                        <span className="flex items-center gap-1">
                          <CalendarDays size={11} className="shrink-0" />
                          {dateInfo}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Clock size={11} className="shrink-0" />
                        {formatDate(
                          new Date(item.createdAt),
                          "dd MMM, hh:mm a",
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            );
          })
        )}
      </div>

      {/* Fixed footer — always visible */}
      <div className="shrink-0  rounded-b px-4 py-3 border-t border-border bg-surface grid grid-cols-2 gap-2">
        <Link
          to={PATHS.COMPANY.COMPANY_TIME_ATTENDANCE_PATHS.REGULARIZATION}
          className="flex items-center justify-center gap-1.5 rounded-lg border border-warning/30 bg-warning-muted hover:bg-warning-muted/70 focus-visible:bg-warning-muted/70 py-2.5 transition-colors text-xs font-semibold text-warning outline-hidden"
        >
          {t("company:dashboard.regularization")}
          <ArrowRight size={13} className="rtl:rotate-180" />
        </Link>
        <Link
          to={PATHS.COMPANY.COMPANY_LEAVES_MANAGEMENT_PATHS.ALL_LEAVES}
          className="flex items-center justify-center gap-1.5 rounded-lg border border-info/30 bg-info-muted hover:bg-info-muted/70 focus-visible:bg-info-muted/70 py-2.5 transition-colors text-xs font-semibold text-info outline-hidden"
        >
          {t("company:dashboard.leave_requests")}
          <ArrowRight size={13} className="rtl:rotate-180" />
        </Link>
      </div>
    </DashboardCard>
  );
};
